import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, MapTrifold, Path, Car, Receipt, Trophy, Warning, ArrowRight, House, Sparkle, X } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { shareTripCalendar, emailTripInvite } from "@/lib/calendar";

// Default Leaflet marker fix (Webpack 5 + react-leaflet quirk: bundled icon URLs are wrong)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

import { calcMedicalCredit } from "@/lib/taxCredit";

// CRA simplified mileage rates per-province ($/km). Mirrors backend.
const PROV_RATES = {
  AB: 0.55, BC: 0.57, MB: 0.56, NB: 0.585, NL: 0.605, NS: 0.575,
  NT: 0.725, NU: 0.71, ON: 0.62, PE: 0.575, QC: 0.59, SK: 0.535, YT: 0.705,
};
const PROV_NAMES = {
  AB: "Alberta", BC: "British Columbia", MB: "Manitoba", NB: "New Brunswick",
  NL: "Newfoundland & Labrador", NS: "Nova Scotia", NT: "Northwest Territories",
  NU: "Nunavut", ON: "Ontario", PE: "Prince Edward Island", QC: "Quebec",
  SK: "Saskatchewan", YT: "Yukon",
};

const fmtCAD = (n) =>
  (n || 0).toLocaleString("en-CA", { style: "currency", currency: "CAD", minimumFractionDigits: 2 });

/**
 * MileagePage — primary "Travel & Mileage" tab.
 *
 * Split-pane workflow:
 *  - Left:  destination autocomplete + CRA conditional helpers + save button
 *  - Right: 3 metric cards, threshold progress bar, leaflet map, saved trips
 *
 * Reuses the existing /api/trips collection so the data appears in the
 * Logbook + PDF exports without duplication. Each mileage entry is stored
 * with entry_type="trip", category="Travel".
 */
export default function MileagePage() {
  const { user, isAuthed, refresh } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const homeAddress = user?.home_address || "";
  const homeLat = user?.home_lat;
  const homeLng = user?.home_lng;
  const netIncome = user?.net_income || 0;
  const province = user?.province || "ON";
  const ratePerKm = PROV_RATES[province] || 0.62;

  // -------------------- Load all trips (travel + Rx + premiums) -----
  // We need EVERY entry (not just travel) so the CRA credit calc includes
  // Rx, services, and insurance premiums — matches /log's grand_total.
  const { data: trips = [] } = useQuery({
    queryKey: ["trips"],
    queryFn: async () => (await api.get("/trips")).data,
    enabled: isAuthed,
  });

  // -------------------- Server-computed grand total ------------------
  // Read the SAME summary the /log page uses so both screens report an
  // identical CRA-eligible total. Prevents client/server aggregation drift.
  //
  // Two-step fetch by design:
  //   1. First call → backend returns `available_years[0]` (most recent
  //      year with trips) as the default `year`.
  //   2. Second call → we explicitly refetch WITH that year so the totals
  //      are scoped to a single year — matching /log's post-auto-jump
  //      behavior. Otherwise a no-year call returns all-years-combined
  //      totals, which would silently disagree with /log's year-filtered
  //      Tax Estimate Card.
  const { data: summaryMeta } = useQuery({
    queryKey: ["summary-meta"],
    queryFn: async () => (await api.get("/summary")).data,
    enabled: isAuthed,
  });
  const resolvedYear = summaryMeta?.year;
  const { data: summary } = useQuery({
    queryKey: ["summary", resolvedYear],
    queryFn: async () => (await api.get(`/summary?year=${resolvedYear}`)).data,
    enabled: isAuthed && !!resolvedYear,
  });
  const grandTotalCRA = Number(summary?.totals?.grand_total || 0);

  // -------------------- Aggregate metrics --------------------
  // Local aggregation is used ONLY for the "trip count" and "total km"
  // tiles. The CRA-eligible dollar total comes from the server's
  // /api/summary so it matches /log exactly.
  const metrics = useMemo(() => {
    let tripCount = 0;
    let totalKm = 0;
    (trips || []).forEach((t) => {
      const isTrip = t.entry_type === "trip" || !t.entry_type;
      if (isTrip && (t.total_km || 0) > 0) {
        tripCount++;
        totalKm += t.total_km || 0;
      }
    });
    return { tripCount, totalKm, totalMedicalCRA: grandTotalCRA };
  }, [trips, grandTotalCRA]);

  // Single source of truth for the CRA credit calculation. Mirrors the
  // math used by /log's Tax Estimate Card and backend /api/savings/summary.
  const creditInfo = useMemo(() => calcMedicalCredit({
    totalMedicalExpenses: metrics.totalMedicalCRA,
    netIncome,
    province,
  }), [metrics.totalMedicalCRA, netIncome, province]);

  // Progress vs threshold (drives the visual bar + "$X to go" message)
  const progress = useMemo(() => {
    const pct = creditInfo.threshold > 0
      ? Math.min(100, (metrics.totalMedicalCRA / creditInfo.threshold) * 100)
      : 0;
    return {
      pct,
      remaining: creditInfo.remainingToCredit,
      unlocked: creditInfo.credit,
    };
  }, [metrics.totalMedicalCRA, creditInfo]);

  // Whether to show the "Set your net income" prompt instead of the bar.
  const thresholdReady = !creditInfo.needsIncomeSetup;

  // -------------------- Destination autocomplete --------------------
  const [destQuery, setDestQuery] = useState("");
  const [destResults, setDestResults] = useState([]);
  const [destPicked, setDestPicked] = useState(null);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!destQuery || destQuery.length < 3 || destPicked?.display_name === destQuery) {
      setDestResults([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/geocode?q=${encodeURIComponent(destQuery)}`);
        setDestResults(data?.matches || []);
      } catch {
        setDestResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [destQuery, destPicked]);

  // -------------------- Distance calc --------------------
  const [oneWayKm, setOneWayKm] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);

  useEffect(() => {
    if (!destPicked || !homeLat || !homeLng) {
      setOneWayKm(null);
      return;
    }
    setCalcLoading(true);
    (async () => {
      try {
        const { data } = await api.get(
          `/distance?from_lat=${homeLat}&from_lng=${homeLng}&to_lat=${destPicked.lat}&to_lng=${destPicked.lng}`
        );
        setOneWayKm(data.km);
      } catch {
        setOneWayKm(null);
        toast.error("Couldn't calculate distance — try again");
      } finally {
        setCalcLoading(false);
      }
    })();
  }, [destPicked, homeLat, homeLng]);

  // CRA distance logic
  const craState = useMemo(() => {
    if (oneWayKm == null) return { eligible: false, label: null };
    if (oneWayKm < 40) return { eligible: false, label: "under-40", round: 0 };
    if (oneWayKm >= 80) return { eligible: true, label: "80+", round: oneWayKm * 2 };
    return { eligible: true, label: "40-79", round: oneWayKm * 2 };
  }, [oneWayKm]);

  // -------------------- Optional 80km+ helpers (meals / accommodation) --------------------
  const [includeExtras, setIncludeExtras] = useState(false);
  const [mealCount, setMealCount] = useState(0);
  const [accommodation, setAccommodation] = useState(0);
  const MEAL_RATE = 23; // CRA simplified meal flat-rate per meal
  const extras = (mealCount * MEAL_RATE) + (parseFloat(accommodation) || 0);
  const tripCost = craState.eligible
    ? craState.round * ratePerKm + (craState.label === "80+" && includeExtras ? extras : 0)
    : 0;

  // -------------------- Save trip --------------------
  const [purpose, setPurpose] = useState("");
  const [tripDate, setTripDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [tripTime, setTripTime] = useState(""); // HH:MM — used by .ics calendar sync
  const [syncCalendar, setSyncCalendar] = useState(true); // .ics native-prompt on save
  const [mapOpen, setMapOpen] = useState(false); // mobile: map shown in modal
  const [tripFilter, setTripFilter] = useState("all"); // "all" | "mileage" | "rx" | "vision"

  // Renders the actual Leaflet map — reused for inline (tablet+) and modal (mobile)
  const renderMap = () => (
    <MapContainer
      center={[homeLat, homeLng]}
      zoom={destPicked ? 6 : 11}
      style={{ height: "100%", width: "100%" }}
      scrollWheelZoom={false}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <Marker position={[homeLat, homeLng]} />
      {destPicked && (
        <>
          <Marker position={[destPicked.lat, destPicked.lng]} />
          <Polyline
            positions={[[homeLat, homeLng], [destPicked.lat, destPicked.lng]]}
            pathOptions={{ color: "#2C4C3B", weight: 3, dashArray: "6 6" }}
          />
          <FitBounds points={[[homeLat, homeLng], [destPicked.lat, destPicked.lng]]} />
        </>
      )}
    </MapContainer>
  );

  const saveTrip = useMutation({
    mutationFn: async () => {
      const payload = {
        entry_type: "trip",
        date: tripDate,
        appointment_time: tripTime || null,
        category: "Travel",
        destination: destPicked?.display_name?.split(",")[0] || destQuery,
        purpose: purpose || "Medical appointment",
        total_km: Math.round(craState.round),
        rate_per_km: ratePerKm,
        meals: craState.label === "80+" && includeExtras ? mealCount * MEAL_RATE : 0,
        accommodation: craState.label === "80+" && includeExtras ? parseFloat(accommodation) || 0 : 0,
        notes: `Auto-calculated: ${oneWayKm}km one-way × 2`,
      };
      return (await api.post("/trips", payload)).data;
    },
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ["trips"] });
      // Persistent toast with a REAL anchor — iOS Safari blocks programmatic
      // redirects/popups in PWA mode, but treats a real tap as a user
      // gesture and pops the native Add-to-Calendar sheet inline.
      if (syncCalendar && saved?.id) {
        // Fire both paths in parallel — email + share sheet — for max iOS
        // compatibility (Apple Mail handles .ics attachments most reliably).
        emailTripInvite(saved.id);
        toast.success("Trip saved · invite emailed", {
          duration: 10000,
          description: "Check your inbox to add to Apple Calendar with one tap.",
          action: {
            label: "Add to calendar",
            onClick: () => shareTripCalendar(saved.id),
          },
        });
      } else {
        toast.success("Trip saved");
      }
      // Reset
      setDestQuery("");
      setDestPicked(null);
      setOneWayKm(null);
      setPurpose("");
      setTripTime("");
      setMealCount(0);
      setAccommodation(0);
      setIncludeExtras(false);
    },
    onError: () => toast.error("Could not save trip"),
  });

  const deleteTrip = useMutation({
    mutationFn: async (id) => api.delete(`/trips/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trips"] });
      toast.success("Trip removed");
    },
  });

  // -------------------- Guard: no home address --------------------
  if (!homeAddress || homeLat == null || homeLng == null) {
    return (
      <section className="space-y-6">
        <PageHeader />
        <Card className="rounded-2xl border-[#E3E1D9]">
          <CardContent className="p-8 text-center">
            <House size={36} weight="duotone" className="mx-auto text-[#2C4C3B]" />
            <h2 className="font-[Manrope] text-xl font-semibold mt-3">Set your home address first</h2>
            <p className="text-[#5C6660] mt-2 max-w-md mx-auto">
              We use it as the starting point for every mileage calculation.
              <br />
              <span className="text-xs italic">Stored on your phone. Never shared with MedEasy.</span>
            </p>
            <Button
              data-testid="mileage-set-home"
              onClick={() => navigate("/settings")}
              className="mt-5 rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white"
            >
              <House size={16} className="mr-1.5" /> Set home address in Settings
            </Button>
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <TooltipProvider>
    <section className="space-y-6" data-testid="mileage-page">
      <PageHeader />

      {/* Top metrics row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MetricCard testid="metric-trips" Icon={Path} label="Trips logged" value={metrics.tripCount} />
        <MetricCard testid="metric-km" Icon={Car} label="Total km" value={`${metrics.totalKm.toLocaleString("en-CA")} km`} />
        <MetricCard testid="metric-expense" Icon={Receipt} label="Total medical expense" value={fmtCAD(metrics.totalMedicalCRA)} highlight />
      </div>

      {/* Threshold tracker */}
      <ThresholdBar
        threshold={thresholdReady ? creditInfo.threshold : null}
        totalExpense={metrics.totalMedicalCRA}
        progress={progress}
        combinedRate={creditInfo.combinedRate}
        navigate={navigate}
      />

      {/* Split pane: inputs (left) + map (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-6">
        {/* LEFT — inputs */}
        <Card className="rounded-2xl border-[#E3E1D9]">
          <CardContent className="p-5 sm:p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#2C4C3B] flex items-center justify-center">
                <MapPin weight="fill" size={18} className="text-white" />
              </div>
              <div>
                <h3 className="font-[Manrope] font-semibold text-base">New mileage trip</h3>
                <p className="text-xs text-[#5C6660]">From {homeAddress.split(",")[0]} — auto-calculated</p>
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">Destination address or clinic name</Label>
              <div className="relative mt-2">
                <Input
                  data-testid="mileage-dest-input"
                  value={destPicked?.display_name || destQuery}
                  onChange={(e) => { setDestQuery(e.target.value); setDestPicked(null); }}
                  placeholder="e.g. Health Sciences Centre, Winnipeg"
                  autoComplete="off"
                />
                {searching && (
                  <Sparkle size={16} weight="duotone" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2C4C3B] animate-pulse" />
                )}
                {destResults.length > 0 && !destPicked && (
                  <ul
                    data-testid="mileage-dest-results"
                    className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[#E3E1D9] rounded-xl shadow-lg max-h-64 overflow-y-auto"
                  >
                    {destResults.map((r, i) => (
                      <li key={`${r.place_id}-${i}`}>
                        <button
                          type="button"
                          onClick={() => { setDestPicked(r); setDestQuery(r.display_name); setDestResults([]); }}
                          data-testid={`mileage-dest-pick-${i}`}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-[#F7F6F2] border-b border-[#E3E1D9] last:border-0"
                        >
                          <div className="font-medium truncate">{r.display_name.split(",")[0]}</div>
                          <div className="text-xs text-[#5C6660] truncate">{r.display_name}</div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* CRA conditional badges */}
            {oneWayKm != null && (
              <div className="space-y-2">
                {!craState.eligible && (
                  <div data-testid="cra-under40" className="rounded-xl bg-[#FEF6E4] border border-[#E5C97B]/40 p-3 flex items-start gap-2">
                    <Warning weight="fill" size={18} className="text-[#A07E20] shrink-0 mt-0.5" />
                    <div className="text-sm text-[#6E5717]">
                      <strong>CRA Rule:</strong> One-way trips under 40 km are not eligible for travel deductions.
                      <div className="text-xs text-[#8A6F2A] mt-1">Distance: {oneWayKm} km one-way · won&apos;t be added to your totals.</div>
                    </div>
                  </div>
                )}
                {craState.eligible && (
                  <div data-testid="cra-approved" className="rounded-xl bg-[#E8F0EA] border border-[#2C4C3B]/30 p-3">
                    <div className="flex items-start gap-2">
                      <Trophy weight="fill" size={18} className="text-[#2C4C3B] shrink-0 mt-0.5" />
                      <div className="text-sm text-[#1E2420]">
                        <strong>CRA Approved.</strong> Doubled to {craState.round.toFixed(1)} km round-trip · {fmtCAD(craState.round * ratePerKm)} at {PROV_NAMES[province]} rate ${ratePerKm.toFixed(3)}/km.
                      </div>
                    </div>
                  </div>
                )}
                {craState.label === "80+" && (
                  <div className="rounded-xl border border-[#E3E1D9] bg-white p-3" data-testid="cra-80plus">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <Checkbox
                        data-testid="cra-80plus-toggle"
                        checked={includeExtras}
                        onCheckedChange={(v) => setIncludeExtras(!!v)}
                        className="mt-0.5"
                      />
                      <div className="text-sm text-[#1E2420]">
                        <strong>Trip over 80 km</strong> — add CRA Simplified Meals ($23 each) or accommodation?
                      </div>
                    </label>
                    {includeExtras && (
                      <div className="mt-3 grid grid-cols-2 gap-3 pl-7">
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">Meals (count)</Label>
                          <Input
                            type="number" min={0} max={6} value={mealCount}
                            onChange={(e) => setMealCount(Math.max(0, parseInt(e.target.value || 0)))}
                            data-testid="cra-meals-count"
                            className="mt-1"
                          />
                          <div className="text-[10px] text-[#5C6660] mt-1">= {fmtCAD(mealCount * MEAL_RATE)}</div>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">Accommodation ($)</Label>
                          <Input
                            type="number" min={0} step={0.01} value={accommodation}
                            onChange={(e) => setAccommodation(e.target.value)}
                            data-testid="cra-accommodation"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {calcLoading && (
              <div className="text-sm text-[#5C6660] flex items-center gap-2">
                <Sparkle size={14} weight="duotone" className="animate-pulse text-[#2C4C3B]" /> Calculating distance…
              </div>
            )}

            {/* Date + time + purpose + save */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">Trip date</Label>
                <Input
                  type="date" value={tripDate} onChange={(e) => setTripDate(e.target.value)}
                  data-testid="mileage-trip-date" className="mt-1"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">Time (optional)</Label>
                <Input
                  type="time" value={tripTime} onChange={(e) => setTripTime(e.target.value)}
                  data-testid="mileage-trip-time" className="mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">Purpose</Label>
                <Input
                  value={purpose} onChange={(e) => setPurpose(e.target.value)}
                  placeholder="e.g. MRI scan"
                  data-testid="mileage-purpose" className="mt-1"
                />
              </div>
            </div>

            {/* Calendar sync toggle — opens native iOS/Android calendar prompt on save */}
            <label
              data-testid="mileage-calendar-sync-row"
              className="flex items-start gap-3 rounded-xl border border-[#E3E1D9] bg-[#F7F6F2] p-3 cursor-pointer hover:bg-[#EBE9E0] transition-colors"
            >
              <input
                type="checkbox"
                data-testid="mileage-calendar-sync-toggle"
                checked={syncCalendar}
                onChange={(e) => setSyncCalendar(e.target.checked)}
                className="mt-0.5 accent-[#2C4C3B] w-4 h-4"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-[#1E2420] flex items-center gap-1.5">
                  <Sparkle size={14} weight="duotone" className="text-[#2C4C3B]" />
                  Add to my calendar with a 2-day reminder
                </div>
                <div className="text-[11px] text-[#5C6660] mt-0.5">
                  Opens your phone's native Add-to-Calendar sheet right after saving.
                </div>
              </div>
            </label>

            {craState.eligible && (
              <div className="rounded-xl bg-[#F7F6F2] border border-[#E3E1D9] p-3 flex justify-between items-center">
                <div className="text-xs uppercase tracking-widest text-[#5C6660] font-semibold">Total expense</div>
                <div className="font-[Manrope] font-bold text-2xl text-[#2C4C3B]" data-testid="mileage-trip-total">
                  {fmtCAD(tripCost)}
                </div>
              </div>
            )}

            <Button
              data-testid="mileage-save-trip"
              onClick={() => saveTrip.mutate()}
              disabled={!craState.eligible || saveTrip.isPending}
              className="w-full h-12 rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white disabled:opacity-40"
            >
              {saveTrip.isPending ? "Saving…" : "Save trip"}
              <ArrowRight size={16} className="ml-1.5" />
            </Button>
          </CardContent>
        </Card>

        {/* RIGHT — leaflet map. Inline on tablet+ for context; collapsed
            behind a button on mobile to save vertical real-estate. */}
        <div className="lg:contents">
          {/* Mobile: button → modal */}
          <div className="lg:hidden">
            <button
              type="button"
              onClick={() => setMapOpen(true)}
              data-testid="mileage-view-route"
              className="w-full rounded-2xl border border-[#E3E1D9] bg-white p-3 flex items-center justify-between hover:bg-[#F7F6F2] transition-colors"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-[#1E2420]">
                <MapTrifold size={18} weight="duotone" className="text-[#2C4C3B]" />
                View trip route
              </span>
              <ArrowRight size={16} className="text-[#5C6660]" />
            </button>
          </div>
          {/* Tablet+: inline map */}
          <Card className="hidden lg:block rounded-2xl border-[#E3E1D9] overflow-hidden">
            <div className="h-[480px] relative" data-testid="mileage-map">
              {renderMap()}
            </div>
          </Card>
        </div>
      </div>

      {/* Saved trips table */}
      <SavedTripsTable trips={trips} ratePerKm={ratePerKm} filter={tripFilter} onFilter={setTripFilter} onDelete={(id) => deleteTrip.mutate(id)} />

      {/* Mobile map modal */}
      <Dialog open={mapOpen} onOpenChange={setMapOpen}>
        <DialogContent className="max-w-[95vw] h-[80vh] p-0 overflow-hidden" data-testid="mileage-map-modal">
          <DialogHeader className="px-4 pt-4 pb-2 flex flex-row items-center justify-between space-y-0">
            <DialogTitle className="flex items-center gap-2 text-base">
              <MapTrifold size={18} weight="duotone" className="text-[#2C4C3B]" />
              Trip route
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full">{mapOpen && renderMap()}</div>
        </DialogContent>
      </Dialog>
    </section>
    </TooltipProvider>
  );
}

// ===========================================================================
// Sub-components
// ===========================================================================

function PageHeader() {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-[#5C6660] font-semibold">CRA Line 33099</div>
      <h1 className="font-[Manrope] text-4xl font-bold tracking-tight mt-2">Travel &amp; Mileage</h1>
      <p className="text-[#5C6660] mt-2">Auto-calculate medical-trip mileage from your home address. CRA-compliant.</p>
    </div>
  );
}

function MetricCard({ Icon, label, value, highlight, testid }) {
  return (
    <Card className={`rounded-2xl border-[#E3E1D9] ${highlight ? "bg-[#2C4C3B] text-white" : "bg-white"}`} data-testid={testid}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center gap-2">
          <Icon size={18} weight="duotone" className={highlight ? "text-[#D4A373]" : "text-[#2C4C3B]"} />
          <div className={`text-[10px] uppercase tracking-widest font-semibold ${highlight ? "text-white/70" : "text-[#5C6660]"}`}>{label}</div>
        </div>
        <div className="font-[Manrope] font-bold text-2xl sm:text-3xl mt-2">{value}</div>
      </CardContent>
    </Card>
  );
}

function ThresholdBar({ threshold, totalExpense, progress, combinedRate, navigate }) {
  if (threshold == null) {
    return (
      <Card className="rounded-2xl border-[#E3E1D9] bg-[#FEF6E4]/70" data-testid="threshold-prompt">
        <CardContent className="p-5 flex items-start gap-3">
          <Warning weight="fill" size={20} className="text-[#A07E20] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-[#1E2420]">Set your net income in Settings to see your exact threshold target.</div>
            <div className="text-xs text-[#5C6660] mt-1">CRA medical credit unlocks at the lesser of 3% × your net income or $2,834 (2025).</div>
          </div>
          <Button onClick={() => navigate("/settings")} className="rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white" data-testid="threshold-go-settings">
            Set now
          </Button>
        </CardContent>
      </Card>
    );
  }

  const isMet = totalExpense >= threshold;
  return (
    <Card className={`rounded-2xl border-[#E3E1D9] ${isMet ? "bg-gradient-to-br from-[#2C4C3B] to-[#1F3729] text-white" : "bg-white"}`} data-testid="threshold-bar-card">
      <CardContent className="p-3 sm:p-5">
        {/* Compact horizontal row on mobile; vertical stack on tablet+ */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:block">
          <div className="min-w-0 flex-shrink-0">
            <div className={`text-[10px] uppercase tracking-widest font-semibold ${isMet ? "text-white/70" : "text-[#5C6660]"}`}>
              CRA threshold
            </div>
            <div className="font-[Manrope] font-bold text-base sm:text-xl mt-0.5 sm:mt-1">
              {fmtCAD(totalExpense)}<span className={`font-normal ${isMet ? "text-white/60" : "text-[#5C6660]"}`}> / {fmtCAD(threshold)}</span>
            </div>
          </div>
          <div className={`flex-shrink-0 text-[11px] sm:text-xs font-semibold ${isMet ? "text-[#D4A373]" : "text-[#2C4C3B]"}`}>
            {progress.pct.toFixed(1)}%
          </div>
          {isMet && (
            <div className="text-right ml-auto" data-testid="threshold-unlocked">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-white/70 font-semibold">Unlocked</div>
              <div className="font-[Manrope] font-bold text-lg sm:text-2xl text-[#D4A373]">{fmtCAD(progress.unlocked)}</div>
            </div>
          )}
        </div>
        <div className={`h-2 sm:h-3 rounded-full overflow-hidden mt-2 sm:mt-3 ${isMet ? "bg-white/15" : "bg-[#EBE9E0]"}`}>
          <div
            className="h-full transition-[width] duration-700 ease-out"
            style={{
              width: `${progress.pct}%`,
              background: isMet ? "linear-gradient(90deg,#D4A373,#E5B687)" : "#2C4C3B",
            }}
            data-testid="threshold-bar-fill"
          />
        </div>
        <div className={`text-[11px] sm:text-xs mt-2 sm:mt-3 ${isMet ? "text-white/80" : "text-[#5C6660]"}`}>
          {isMet ? (
            <>🎉 Threshold met! Each extra dollar earns ~{Math.round((combinedRate || 0.24) * 100)}% back.</>
          ) : (
            <>Need <strong className="text-[#1E2420]">{fmtCAD(progress.remaining)}</strong> more before tax savings unlock.</>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points && points.length >= 2) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [map, points]);
  return null;
}

function SavedTripsTable({ trips, ratePerKm, filter, onFilter, onDelete }) {
  // Categorize every trip into one of the chip buckets so the filter is honest.
  // Mileage = travel entries with km. Rx = prescription entries. Vision/Hearing
  // = trips tagged with the Device CRA category (includes hearing aids,
  // glasses, contacts, mobility aids).
  const bucketOf = (t) => {
    if (t.entry_type === "prescription") return "rx";
    if (t.category === "Device") return "vision";
    if ((t.total_km || 0) > 0) return "mileage";
    return "other";
  };
  const visible = (trips || []).filter((t) => filter === "all" || bucketOf(t) === filter);

  return (
    <Card className="rounded-2xl border-[#E3E1D9]" data-testid="saved-trips-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="font-[Manrope] font-semibold text-base">Historical expense logbook</h3>
          <div className="flex flex-wrap gap-1.5" data-testid="trips-filter-chips">
            {[
              { id: "all", label: "All" },
              { id: "mileage", label: "🛣️ Mileage" },
              { id: "rx", label: "💊 Prescriptions" },
              { id: "vision", label: "🦷 Vision/Hearing" },
            ].map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onFilter(c.id)}
                data-testid={`chip-${c.id}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  filter === c.id
                    ? "bg-[#2C4C3B] text-white"
                    : "bg-[#F7F6F2] text-[#5C6660] hover:bg-[#EBE9E0]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
        {visible.length === 0 ? (
          <div className="text-center text-sm text-[#5C6660] py-6" data-testid="trips-empty">
            {filter === "all" ? "No trips logged yet." : `No entries in this category yet.`}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="trips-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#5C6660] border-b border-[#E3E1D9]">
                  <th className="px-3 py-2 font-semibold">Date</th>
                  <th className="px-3 py-2 font-semibold">Purpose</th>
                  <th className="px-3 py-2 font-semibold">Destination</th>
                  <th className="px-3 py-2 font-semibold text-right">Round-trip km</th>
                  <th className="px-3 py-2 font-semibold text-right">Total</th>
                  <th className="px-3 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((t) => {
                  const km = t.total_km || 0;
                  const rate = t.rate_per_km || ratePerKm;
                  const extras = (t.meals || 0) + (t.parking || 0) + (t.accommodation || 0) +
                                 (t.tolls || 0) + (t.gas || 0) + (t.taxi || 0) +
                                 (t.rental || 0) + (t.other_amount || 0);
                  const drugOOP = (t.service_total || 0) > 0
                    ? Math.max(0, (t.service_total || 0) - (t.service_covered || 0))
                    : (t.drug_amount || 0);
                  const eligibleKm = (t.one_way_km || 0) >= 40 ? km : 0;
                  const total = eligibleKm * rate + extras + drugOOP;
                  return (
                    <tr key={t.id} className="border-b border-[#E3E1D9] last:border-0 hover:bg-[#F7F6F2]" data-testid={`trip-row-${t.id}`}>
                      <td className="px-3 py-2.5 whitespace-nowrap">{t.date}</td>
                      <td className="px-3 py-2.5 truncate max-w-[200px]">{t.purpose || t.drug_name || "—"}</td>
                      <td className="px-3 py-2.5 truncate max-w-[200px]">{t.destination || t.drug_pharmacy || "—"}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{km > 0 ? km.toFixed(0) : "—"}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-[#2C4C3B]">{total.toFixed(2)}</td>
                      <td className="px-3 py-2.5 text-right">
                        <button
                          onClick={() => onDelete(t.id)}
                          data-testid={`trip-delete-${t.id}`}
                          className="text-[#5C6660] hover:text-[#1E2420]"
                          aria-label="Delete"
                        >
                          <X size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
